import React, { useState, useCallback } from "react";
import { useLanguage } from "../utils/LanguageContext";
import { useTheme } from "../utils/ThemeContext";

/* ── How many cards to show before "Show More" kicks in ── */
const INITIAL_VISIBLE = 6;

/* ── Chevron SVG (no extra dep) ── */
const ChevronDown = ({ flipped }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: flipped ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Projects = () => {
  const { language, t } = useLanguage();
  const { theme } = useTheme();

  /* Use index instead of localized strings to prevent i18n state-sync issues */
  const [activeIndex, setActiveIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  /* Reset show-all whenever category changes */
  const handleCategoryChange = useCallback((index) => {
    setActiveIndex(index);
    setShowAll(false);
  }, []);

  const categories = t.projectCategories;
  const englishCategories = ["All", "Full-Stack", "Frontend", "Mobile", "API"];

  /* Filter cards by selected category index */
  const filteredCards =
    activeIndex === 0
      ? t.cards
      : t.cards.filter((c) => c.category === englishCategories[activeIndex]);

  /* Slice for progressive disclosure */
  const visibleCards = showAll ? filteredCards : filteredCards.slice(0, INITIAL_VISIBLE);
  const hasMore = filteredCards.length > INITIAL_VISIBLE;

  return (
    <section className="flex flex-col" id="projects">

      {/* ── Section accent bar ── */}
      <div className="section-accent reveal reveal-left sm:mt-10 sm:ml-2" />

      {/* ── Section heading ── */}
      <div className="reveal reveal-left reveal-delay-1">
        <h2 className={`${theme === "dark" ? "title-dark" : "title"}`}>
          {t.projectTitle}
        </h2>
      </div>

      {/* ── Filter Tabs ── */}
      <div
        className="flex flex-wrap gap-2 mb-10 reveal reveal-left reveal-delay-2"
        role="tablist"
        aria-label={language === "en" ? "Project categories" : "Catégories de projets"}
      >
        {categories.map((cat, index) => {
          const count =
            index === 0
              ? t.cards.length
              : t.cards.filter((c) => c.category === englishCategories[index]).length;
          const isActive = activeIndex === index;
          return (
            <button
              key={cat}
              role="tab"
              aria-selected={isActive}
              onClick={() => handleCategoryChange(index)}
              className={`filter-tab${isActive ? " active" : ""}`}
              id={`tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
              <span className="project-count-badge" aria-hidden="true">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Cards Grid ── */}
      <div
        className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-x-8 gap-y-10"
        role="tabpanel"
        aria-live="polite"
      >
        {visibleCards.map(({ image, info, technologies, title, id, code, live, alt, category }, index) => (
          <article
            key={`${activeIndex}-${id}`}   /* key change triggers re-mount → CSS animation */
            className="card-pop card-glow group h-full shadow-xl rounded-2xl bg-gray-800 dark:bg-white dark:shadow-lg dark:border dark:border-slate-200 overflow-hidden flex flex-col cursor-pointer"
            style={{ animationDelay: `${index * 55}ms` }}
          >
            {/* Image */}
            <div className="w-full aspect-video overflow-hidden bg-slate-700 dark:bg-slate-100">
              <img
                src={image}
                alt={alt || `Screenshot of ${title} project`}
                className="card-img-zoom w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="px-5 pb-5 pt-4 flex flex-col flex-1">
              {/* Category pill */}
              <span className="self-start mb-2 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider bg-violet-500/10 text-violet-400 dark:bg-violet-100 dark:text-violet-700 border border-violet-500/20 dark:border-violet-200">
                {category}
              </span>

              <h3 className="text-lg font-bold text-white dark:text-slate-900 mb-1">
                {title}
              </h3>
              <p className="text-sm text-gray-400 dark:text-slate-600 leading-relaxed mb-3 flex-1">
                {info}
              </p>

              {/* Tech label */}
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-500 mb-2">
                {language === "en" ? "Stack" : "Technologies"}
              </p>

              {/* Tech badges */}
              <ul className="flex flex-row flex-wrap gap-1.5 mb-4">
                {technologies.map((tech, i) => (
                  <li
                    key={i}
                    className="tech-badge text-gray-300 dark:text-slate-600 bg-slate-900/60 dark:bg-slate-100 border border-slate-700 dark:border-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium"
                  >
                    {tech}
                  </li>
                ))}
              </ul>

              {/* Action buttons */}
              {(live || code) && (
                <div className="flex gap-2 mt-auto">
                  {live && (
                    <a
                      href={live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center text-sm bg-gradient-to-r from-violet-500 to-indigo-600 text-white px-3 py-2 rounded-lg font-medium hover:opacity-90 hover:shadow-lg hover:shadow-violet-500/20 transition-all duration-200"
                    >
                      {language === "en" ? "Live Demo" : "Démo"}
                    </a>
                  )}
                  {code && (
                    <a
                      href={code}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center text-sm bg-slate-700 dark:bg-slate-100 hover:bg-slate-600 dark:hover:bg-slate-200 text-white dark:text-slate-700 border border-slate-600 dark:border-slate-300 px-3 py-2 rounded-lg font-medium transition-all duration-200"
                    >
                      {language === "en" ? "Source" : "Code"}
                    </a>
                  )}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* ── Show More / Show Less ── */}
      {hasMore && (
        <div className="flex justify-center mt-12">
          <button
            className="show-more-btn"
            onClick={() => setShowAll((prev) => !prev)}
            aria-expanded={showAll}
          >
            {showAll ? t.projectShowLess : t.projectShowMore}
            <ChevronDown flipped={showAll} />
          </button>
        </div>
      )}
    </section>
  );
};

export default Projects;
